import { z } from 'zod';
import { getAllRowIds } from '../tableFieldSelection/tableFieldSelectionUseCase';

/**
 * Build Zod schema for a table field. When validation.required is true, every row
 * must have at least one selection in a selectable column.
 * @param {FormTableData | null} tableData - Table structure (headers, groups)
 * @param {FormItemValidation | null} validation - Field validation (e.g. required)
 * @returns {z.ZodTypeAny} Zod schema for the table field value
 */
export function getTableFieldSchema(tableData, validation) {
  const baseSchema = z.array(z.object({ rowId: z.string(), colId: z.string() })).default([]);
  if (validation?.required !== true || !tableData) {
    return baseSchema;
  }
  const selectableColIds = new Set(
    (tableData.headers || [])
      .filter((h) => h.selectable !== false)
      .map((h) => h.colId),
  );
  if (selectableColIds.size === 0) {
    return baseSchema;
  }
  return baseSchema.refine(
    (value) => {
      const rowIds = getAllRowIds(tableData);
      const selectionByRow = /** @type {Record<string, string>} */ ({});
      (value || []).forEach((s) => { selectionByRow[s.rowId] = s.colId; });
      return rowIds.every(
        (rowId) => selectableColIds.has(selectionByRow[rowId]),
      );
    },
    { message: 'form.tableAllRowsRequired' },
  );
}

/**
 * Minimal field object for validation (contains only properties needed for zodSchema)
 * @typedef {object} FieldForValidation
 * @property {string} id - Field ID
 * @property {string} type - Field type
 * @property {FormItemValidation | null} [validation] - Validation rules
 * @property {string | string[] | null} [default] - Default value
 */

/**
 * Field type mapping configuration
 * @type {Record<string, FieldTypeConfig>}
 */
export const FIELD_TYPE_MAP = {
  text: {
    component: 'text',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      const schema = field.validation?.required
        ? z.string().min(1, 'form.required')
        : z.string().optional();

      // Note: not_null is not in OpenAPI spec, only required
      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
  },

  int: {
    component: 'int',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      let schema = z.coerce.number().int('form.integer');

      if (field.validation?.required) {
        // For required int fields, we don't allow undefined
        // @ts-ignore
        schema = schema.min(-Infinity);
      } else {
        schema = schema.optional();
      }

      return schema;
    },
    parseValue: (/** @type {string} */ value) => {
      if (value === '' || value == null) return undefined;
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    },
    formatValue: (/** @type {string} */ value) => (value != null ? String(value) : ''),
  },

  float: {
    component: 'int',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      let schema = z.coerce.number('form.number');

      if (field.validation?.required) {
        // @ts-ignore
        schema = schema.min(-Infinity);
      } else {
        schema = schema.optional();
      }

      return schema;
    },
    parseValue: (/** @type {string} */ value) => {
      if (value === '' || value == null) return undefined;
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    },
    formatValue: (/** @type {string} */ value) => (value != null ? String(value) : ''),
  },

  enum: {
    component: 'select',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      const schema = field.validation?.required
        ? z.string().min(1, 'form.required')
        : z.string().optional();

      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
    requiresController: true,
  },

  color: {
    component: 'color',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      let schema = z.string().regex(
        /^#(?:[0-9a-fA-F]{3}){1,2}$/,
        'form.color',
      );

      if (!field.validation?.required) {
        schema = schema.optional();
      }

      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '#FFFFFF',
    formatValue: (/** @type {string} */ value) => value || '#FFFFFF',
  },

  email: {
    component: 'email',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      const schema = field.validation?.required
        ? z.string().min(1, 'form.required').pipe(z.email('form.email'))
        : z.union([z.literal(''), z.email('form.email')]);

      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
  },

  textarea: {
    component: 'textarea',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      const schema = field.validation?.required
        ? z.string().min(1, 'form.required')
        : z.string().optional();

      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
  },

  datetime: {
    component: 'date',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      let schema = z.string();

      if (!field.validation?.required) {
        schema = schema.optional();
      }

      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
  },

  time: {
    component: 'time',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      let schema = z.string();

      if (!field.validation?.required) {
        schema = schema.optional();
      }

      return schema;
    },
    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
    requiresController: true,
  },

  file: {
    component: 'file',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      // Value is non-empty string (filename) or null; empty string is normalized to null by parseValue
      /** @type {import('zod').ZodTypeAny} */
      const nonEmptyStringOrNull = z.union([
        z.string().min(1, 'form.required'),
        z.null(),
      ]);
      if (field.validation?.required) {
        return nonEmptyStringOrNull.refine((v) => v != null, 'form.required');
      }
      return nonEmptyStringOrNull.optional();
    },
    parseValue: (/** @type {string | null} */ value) => (value == null || value === '' ? null : value),
    formatValue: (/** @type {string | null} */ value) => value ?? '',
    requiresController: true,
  },

  wysiwyg: {
    component: 'wysiwyg',
    zodSchema: () => z.any(), // TODO LOIC: add the correct schema when connect to api

    parseValue: (/** @type {string} */ value) => value || '',
    formatValue: (/** @type {string} */ value) => value || '',
    requiresController: true,
  },

  boolean: {
    component: 'boolean',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      /** @type {import('zod').ZodTypeAny} */
      let schema = z.enum(['oui', 'non'], {
        message: 'form.boolean',
      });

      if (!field.validation?.required) {
        schema = schema.optional();
      }

      return schema;
    },
    parseValue: (/** @type {string} */ value) => {
      // Convert value to 'oui' or 'non', default to 'oui' if invalid
      if (value === 'oui' || value === 'non') {
        return value;
      }
      return 'oui';
    },
    formatValue: (/** @type {string} */ value) => value || 'oui',
    requiresController: true,
  },

  mergeTable: {
    component: 'mergeTable',
    zodSchema: (/** @type {FieldForValidation & { table:FormTableData | null }} */ field) => getTableFieldSchema(field?.table ?? null, field?.validation ?? null),
    parseValue: (/** @type {any[]} */ value) => {
      if (!Array.isArray(value)) return [];
      return value.map((s) => {
        if (typeof s === 'object' && s !== null) return s;
        try { return JSON.parse(s); } catch { return null; }
      }).filter(Boolean);
    },
    formatValue: (/** @type {FormTableSelection[]} */ value) => (Array.isArray(value) ? value : []),
    requiresController: true,
  },

  entity: {
    component: 'entity',
    zodSchema: () => z.any(),
    parseValue: (/** @type {{ values?: string[], displayedValues?: string[] } | null} */ value) => ({
      values: Array.isArray(value?.values) ? value.values : [],
      displayedValues: Array.isArray(value?.displayedValues) ? value.displayedValues : [],
    }),
    formatValue: (/** @type {{ values?: string[], displayedValues?: string[] } | null} */ value) => ({
      values: Array.isArray(value?.values) ? value.values : [],
      displayedValues: Array.isArray(value?.displayedValues) ? value.displayedValues : [],
    }),
    requiresController: true,
  },

  checkboxList: {
    component: 'checkboxList',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      const schema = z.array(z.string());
      return field.validation?.required
        ? schema.min(1, 'form.required')
        : schema.default([]);
    },
    parseValue: (/** @type {string[]} */ value) => {
      if (!Array.isArray(value)) return [];
      return value.map((item) => String(item));
    },
    formatValue: (/** @type {string[]} */ value) => (Array.isArray(value) ? value : []),
    requiresController: true,
  },

  checkbox: {
    component: 'checkbox',
    zodSchema: (/** @type {FieldForValidation} */ field) => (field.validation?.required
      ? z.boolean().refine((v) => v === true, 'form.required')
      : z.boolean().default(false)),
    parseValue: (/** @type {boolean} */ value) => Boolean(value),
    formatValue: (/** @type {boolean} */ value) => Boolean(value),
    requiresController: true,
  },

  radio: {
    component: 'radio',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      const schema = z.string();
      return field.validation?.required
        ? schema.min(1, 'form.required')
        : schema.optional();
    },
    parseValue: (/** @type {string} */ value) => value ?? '',
    formatValue: (/** @type {string} */ value) => value ?? '',
  },

  radioList: {
    component: 'radioList',
    zodSchema: (/** @type {FieldForValidation} */ field) => {
      const schema = z.string();
      return field.validation?.required
        ? schema.min(1, 'form.required')
        : schema.optional();
    },
    parseValue: (/** @type {string} */ value) => value ?? '',
    formatValue: (/** @type {string} */ value) => value ?? '',
    requiresController: true,
  },

  grid: {
    component: 'grid',
    zodSchema: () => z.any(),
    parseValue: () => undefined,
    formatValue: () => undefined,
  },

};
